import React, { useState } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  InfoWindow,
} from "@react-google-maps/api";
import { Box, Typography, Button } from "@mui/material";
import bgBlock from "../assets/seven/backgroundImage.png";

const containerStyle = {
  width: "100%",
  height: "100%",
  minHeight: "400px",
  borderRadius: "8px",
};

// Global styles to override Google Maps default InfoWindow padding and close button
const globalMapStyles = `
  /* Remove all padding and set border radius for the main InfoWindow container */
  .gm-style-iw {
    padding: 0 !important;
    border-radius: 12px !important;
    overflow: hidden !important;
  }
  
  /* Target the exact internal container Google uses to wrap content */
  .gm-style-iw-c {
    padding: 0 !important;
    border-radius: 12px !important;
    overflow: hidden !important;
  }
  
  /* Target the content wrapper inside the InfoWindow */
  .gm-style-iw-d {
    overflow: hidden !important;
    padding: 0 !important;
    margin: 0 !important;
    border-radius: 12px !important;
    /* Ensure no scrollbars */
    max-height: none !important;
  }
  
  /* Remove any nested padding/margin that might be pushing the image down */
  .gm-style .gm-style-iw-c .gm-style-iw-d {
    overflow: hidden !important;
    padding: 0 !important;
  }
  
  .gm-style-iw-d > div {
    margin: 0 !important;
    padding: 0 !important;
  }

  /* Force the close button wrapper to absolute positioning so it doesn't affect document flow */
  button.gm-ui-hover-effect {
    position: absolute !important;
    top: 6px !important;
    right: 6px !important;
    background-color: rgba(0, 0, 0, 0.4) !important;
    border-radius: 50% !important;
    width: 24px !important;
    height: 24px !important;
    z-index: 10 !important;
    margin: 0 !important;
    padding: 0 !important;
    border: none !important;
  }
  
  /* Style the actual X icon inside the close button */
  button.gm-ui-hover-effect span {
    width: 14px !important;
    height: 14px !important;
    margin: 5px !important;
    background-color: white !important;
  }
  
  /* Hide the default white background block for the close button if Google injects it */
  .gm-style-iw-tc::after {
    /* Keeps the little pointer arrow pointing to the pin */
  }
`;

// Center map on Monterrey
const center = {
  lat: 25.6866,
  lng: -100.3161,
};

export const sevenElevenStores = [
  // Zona Centro
  { id: 1, name: "7-Eleven Pino Suárez", address: "Pino Suárez y Melchor Ocampo #932 sur, Centro", lat: 25.6685, lng: -100.3168, hours: "L-D: 24 Horas" },
  { id: 2, name: "7-Eleven Emilio Carranza", address: "Emilio Carranza 808, Centro", lat: 25.6723, lng: -100.3129, hours: "L-D: 24 Horas" },
  { id: 3, name: "7-Eleven Padre Mier", address: "Padre Mier 128-Pte, Centro", lat: 25.6690, lng: -100.3148, hours: "L-D: 24 Horas" },
  { id: 4, name: "7-Eleven Ruperto Martínez", address: "Ruperto Martínez y Villagrán 130, Centro", lat: 25.6750, lng: -100.3200, hours: "L-D: 24 Horas" },
  { id: 5, name: "7-Eleven 15 de Mayo", address: "15 de Mayo 556-Pte, Centro", lat: 25.6705, lng: -100.3160, hours: "L-D: 24 Horas" },
  // Zona Cumbres y Poniente
  { id: 6, name: "7-Eleven Cumbres 2o. Sector", address: "Av. Paseo de los Leones 2247", lat: 25.7225, lng: -100.3700, hours: "L-D: 24 Horas" },
  { id: 7, name: "7-Eleven Real Cumbres", address: "Real de Cumbres 1900", lat: 25.7483, lng: -100.3951, hours: "L-D: 24 Horas" },
  { id: 8, name: "7-Eleven Lomas Cumbres", address: "Av. Abraham Lincoln 5000", lat: 25.7333, lng: -100.3800, hours: "L-D: 24 Horas" },
  { id: 9, name: "7-Eleven Colinas del Valle", address: "Lote 37 y 38, Colinas del Valle 2° Sector", lat: 25.6896, lng: -100.3804, hours: "L-D: 24 Horas" },
  { id: 10, name: "7-Eleven Cumbres 1º Sector", address: "26A. Avenida 271", lat: 25.7196, lng: -100.3601, hours: "L-D: 24 Horas" },
  // Zona San Nicolás de los Garza
  { id: 11, name: "7-Eleven Av. Munich", address: "Av. Munich 195-Int. B y K, Cuauhtémoc", lat: 25.7315, lng: -100.3060, hours: "L-D: 24 Horas" },
  { id: 12, name: "7-Eleven Topo Chico", address: "Av. Topo Chico 810, Anáhuac", lat: 25.7420, lng: -100.3000, hours: "L-D: 24 Horas" },
  { id: 13, name: "7-Eleven Iturbide", address: "Benito Juárez Ote. 103, Iturbide", lat: 25.7505, lng: -100.2850, hours: "L-D: 24 Horas" },
  { id: 14, name: "7-Eleven San Nicolás Centro", address: "Porfirio Díaz Esq. C. Jerónimo Treviño 200, Centro de San Nicolás", lat: 25.7466, lng: -100.2916, hours: "L-D: 24 Horas" },
  { id: 15, name: "7-Eleven Las Puentes", address: "Av. San Nicolás 406, Las Puentes 2do Sector", lat: 25.7390, lng: -100.2780, hours: "L-D: 24 Horas" },
];

const MapComponent = () => {
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    // Replace with your actual Google Maps API Key
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  const [map, setMap] = React.useState(null);
  const [selectedStore, setSelectedStore] = useState(null);

  const onLoad = React.useCallback(function callback(map) {
    // You could adjust bounds here if needed
    setMap(map);
  }, []);

  const onUnmount = React.useCallback(function callback(map) {
    setMap(null);
  }, []);

  if (!isLoaded)
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          minHeight: "400px",
          bgcolor: "#f0f0f0",
        }}
      >
        Cargando mapa...
      </Box>
    );

  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={center}
      zoom={11}
      onLoad={onLoad}
      onUnmount={onUnmount}
      options={{
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      }}
    >
      <style>{globalMapStyles}</style>
      
      {sevenElevenStores.map((store) => (
        <Marker
          key={store.id}
          position={{ lat: store.lat, lng: store.lng }}
          onClick={() => setSelectedStore(store)}
          icon={{
            url: require("../assets/seven/logo-seven.png"), // Reusing logo as a pin for now until exact pin image is uploaded
            scaledSize: new window.google.maps.Size(36, 36),
            origin: new window.google.maps.Point(0, 0),
            anchor: new window.google.maps.Point(18, 18),
          }}
        />
      ))}

      {selectedStore && (
        <InfoWindow
          position={{ lat: selectedStore.lat, lng: selectedStore.lng }}
          onCloseClick={() => setSelectedStore(null)}
          options={{
            pixelOffset: new window.google.maps.Size(0, -20)
          }}
        >
          <Box sx={{ 
            minWidth: "200px", 
            maxWidth: "250px", 
            overflow: "hidden", 
            borderRadius: "8px" 
          }}>
            {/* Header Image */}
            <Box
              sx={{
                width: "100%",
                height: "80px",
                backgroundImage: `url(${bgBlock})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                position: "relative",
              }}
            >
              {/* Overlay to match the store card look */}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(0, 121, 83, 0.6)", // 7-Eleven green overlay
                  zIndex: 1,
                }}
              />
            </Box>

            {/* Content Container */}
            <Box sx={{ p: 1.5, textAlign: "center", bgcolor: "white" }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5, color: "#1a1a1a" }}>
                {selectedStore.name}
              </Typography>
              <Typography variant="body2" sx={{ color: "#666", mb: 0.5, fontSize: "0.8rem", lineHeight: 1.2 }}>
                {selectedStore.address}
              </Typography>
              <Typography variant="body2" sx={{ color: "#2e7d32", mb: 1.5, fontSize: "0.75rem", fontWeight: "bold" }}>
                {selectedStore.hours}
              </Typography>
              <Button 
                variant="contained" 
                size="small"
                fullWidth
                sx={{ 
                  backgroundColor: "#E1131B", // 7-Eleven Red
                  color: "white",
                  fontWeight: "bold",
                  borderRadius: "20px",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#b81016",
                  }
                }}
                onClick={() => {
                  window.location.href = "https://pide.mennu.mx/7-eleven";
                }}
              >
                Ir a tienda
              </Button>
            </Box>
          </Box>
        </InfoWindow>
      )}
    </GoogleMap>
  );
};

export default React.memo(MapComponent);
